import Phaser from 'phaser';
import { ITEM_DEFINITIONS, SLOT_LABELS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { InventoryManager } from '../systems/InventoryManager';
import { RunManager } from '../systems/RunManager';
import { CLASS_DEFINITIONS } from '../constants';
import { ItemInstance, PersonalSlot, PERSONAL_SLOTS } from '../types/Item.types';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { RARITY_COLORS, formatBonuses, getItemColorNum } from '../ui/InventoryUI';
import { ensureTouchTarget } from '../ui/MobileTouchUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE D'ÉQUIPEMENT ===
// Permet d'équiper/déséquiper entre les combats.
// Onglets par membre d'équipe pour gérer l'équipement de chacun.
// Utilise InventoryManager comme source de vérité unique.

const HEADER_H = 95;

export class EquipScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private run!: RunManager;
  private inv!: InventoryManager;
  private returnScene: string = 'MapScene';
  private returnData: Record<string, unknown> = {};
  private activeTab = 0;
  /** Bug fix v0.38.13 — anti-ghost-click mobile (cf. RunPrepScene) */
  private isProcessing = false;
  private popupElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'EquipScene' });
  }

  /** Demande un redraw au prochain tick + garde anti-ghost-click 300ms.
   * Doit être appelé DANS un pointerdown handler — voir commentaire RunPrepScene. */
  private redrawSoon(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.time.delayedCall(0, () => this.drawUI());
    this.time.delayedCall(300, () => { this.isProcessing = false; });
  }

  private closePopup(): void {
    this.popupElements.forEach(e => e.destroy());
    this.popupElements = [];
  }

  init(data: { run?: RunManager; returnTo?: string; returnData?: Record<string, unknown> }): void {
    this.run = data?.run ?? new RunManager();
    this.inv = this.run.getInventory();
    this.returnScene = data?.returnTo ?? 'MapScene';
    this.returnData = data?.returnData ?? {};
    this.activeTab = 0;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  private drawUI(): void {
    this.closePopup();
    this.panel?.destroy();
    this.children.removeAll(true);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // === HEADER ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 12, '⚓ ÉQUIPEMENT', {
      fontSize: '18px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Stats du perso actif
    const team = this.run.getAliveTeam();
    if (team.length > 0) {
      const member = team[Math.min(this.activeTab, team.length - 1)];
      const classDef = CLASS_DEFINITIONS[member.class];
      const stats = this.inv.computeStats(member.id, { ...classDef.stats });
      this.add.text(GAME_CX, 34, `${classDef.emoji} ${member.name}  HP:${stats.maxHp} ATK:${stats.attack} DEF:${stats.defense} PA:${stats.pa} PM:${stats.pm}`, {
        fontSize: '10px', color: '#3498db', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(11);
    }

    // Bouton retour
    const returnLabel = this.returnScene === 'ShopScene' ? '🏪 Boutique' : '🗺️ Carte';
    const backBtn = this.add.text(15, 8, `← ${returnLabel}`, {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnScene, { run: this.run, ...this.returnData }));

    // === ONGLETS PERSONNAGES ===
    if (team.length > 1) {
      const tabAreaW = GAME_W - 30;
      const tabWidth = Math.floor(tabAreaW / team.length);
      for (let t = 0; t < team.length; t++) {
        const m = team[t];
        const cls = CLASS_DEFINITIONS[m.class];
        const isActive = t === this.activeTab;
        const tabX = 15 + t * tabWidth;

        const tabBtn = this.add.text(tabX, 55, `${cls.emoji} ${m.name}`, {
          fontSize: '11px', color: isActive ? '#f1c40f' : '#7f8c8d',
          fontFamily: FONT_BODY, fontStyle: isActive ? 'bold' : 'normal',
          backgroundColor: isActive ? '#1a2a40' : '#151525',
          padding: { x: 8, y: 5 },
        }).setDepth(11);
        if (isActive) {
          this.add.rectangle(tabX + tabBtn.width / 2, 77, tabBtn.width, 2, 0xf1c40f).setDepth(11);
        }
        if (!isActive) {
          tabBtn.setInteractive({ useHandCursor: true });
          tabBtn.on('pointerdown', () => { this.activeTab = t; this.redrawSoon(); });
        }
      }
    }

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);
    let y = 10;

    const activeMember = team[Math.min(this.activeTab, team.length - 1)];
    if (!activeMember) {
      this.panel.add(this.add.text(GAME_CX, 30, 'Aucun membre d\'équipe.', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y = 60;
    } else {
      const memberId = activeMember.id;

      // Slots d'équipement personnel (sans backpack)
      for (const slot of PERSONAL_SLOTS) {
        y = this.drawEquipSlot(memberId, slot, y);
        y += 6;
      }

      // Sac à dos partagé
      y += 10;
      y = this.drawBackpackSlot(y);
      y += 6;

      // Transport (items dans le sac)
      y += 10;
      const transport = this.inv.getTransport();
      const capacity = this.inv.getTransportCapacity();
      this.panel.add(this.add.text(GAME_CX, y, `--- Sac (${transport.length}/${capacity}) ---`, {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 22;

      if (transport.length === 0) {
        this.panel.add(this.add.text(GAME_CX, y, '(vide)', {
          fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }).setOrigin(0.5));
        y += 20;
      } else {
        for (const item of transport) {
          y = this.drawTransportItem(memberId, item, y);
          y += 6;
        }
      }

      // Consommables
      const consumables = this.inv.getConsumables();
      y += 10;
      this.panel.add(this.add.text(GAME_CX, y, `--- Consommables (${consumables.length}/${this.inv.getMaxConsumables()}) ---`, {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 22;
      if (consumables.length === 0) {
        this.panel.add(this.add.text(GAME_CX, y, '(aucun)', {
          fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }).setOrigin(0.5));
        y += 20;
      } else {
        for (const c of consumables) {
          const cDef = CONSUMABLE_DEFINITIONS[c.definitionId];
          if (!cDef) continue;
          this.panel.add(this.add.text(GAME_CX, y + 3, `${cDef.emoji} ${cDef.name} — ${cDef.description}`, {
            fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
            wordWrap: { width: GAME_W - 20 },
          }).setOrigin(0.5, 0));
          y += 22;
        }
      }
    }

    y += 20;
    this.panel.setContentHeight(y);
  }

  private drawEquipSlot(memberId: string, slot: PersonalSlot, y: number): number {
    const rowW = Math.min(540, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const midX = lx + 110;
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const equip = this.inv.getEquipment(memberId);
    const item = equip[slot];
    const label = SLOT_LABELS[slot] ?? slot;

    const bg = this.add.rectangle(GAME_CX, y + 16, rowW, 36, 0x1e2d4a);
    this.panel.add(bg);

    // Toute la ligne est cliquable pour ouvrir la popup de sélection d'équipement
    // (cohérent avec RunPrepScene — permet de swap un item équipé sans le déséquiper d'abord).
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => this.showEquipPopup(memberId, slot));
    bg.on('pointerover', () => bg.setFillStyle(0x253d5a));
    bg.on('pointerout', () => bg.setFillStyle(0x1e2d4a));

    this.panel.add(this.add.text(lx, y + 5, `[${label}]`, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));

    if (item) {
      const def = ITEM_DEFINITIONS[item.definitionId];
      const color = RARITY_COLORS[def.rarity];
      bg.setStrokeStyle(1, getItemColorNum(item.definitionId));

      this.panel.add(this.add.text(midX, y + 3, `★ ${def.emoji} ${def.name}`, {
        fontSize: '10px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
      }));
      const bonuses = formatBonuses(def.bonuses);
      if (bonuses) {
        this.panel.add(this.add.text(midX, y + 17, bonuses, {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }));
      }

      const btn = this.add.text(rx, y + 8, '[X]', {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 4, y: 2 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation(); // empêche d'ouvrir la popup en même temps
        // Déséquiper → remet dans le transport
        const removed = this.inv.unequipFromCharacter(memberId, slot);
        if (removed) this.inv.addToTransport(removed);
        this.redrawSoon();
      });
      ensureTouchTarget(btn);
      this.panel.add(btn);
    } else {
      this.panel.add(this.add.text(midX, y + 5, '(vide — cliquer pour équiper)', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    }

    return y + 36;
  }

  private drawBackpackSlot(y: number): number {
    const rowW = Math.min(540, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const midX = lx + 110;
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const bag = this.inv.getBackpack();
    const bg = this.add.rectangle(GAME_CX, y + 17, rowW, 36, 0x1e2d4a).setStrokeStyle(1, 0x2a3a5c);
    this.panel.add(bg);

    // Toute la ligne cliquable pour choisir/swap un sac (même logique que les autres slots)
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => this.showBackpackPopup());
    bg.on('pointerover', () => bg.setFillStyle(0x253d5a));
    bg.on('pointerout', () => bg.setFillStyle(0x1e2d4a));

    this.panel.add(this.add.text(lx, y + 5, '[🎒 Sac]', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));

    if (bag) {
      const bagDef = ITEM_DEFINITIONS[bag.definitionId];
      const bagColor = RARITY_COLORS[bagDef.rarity] ?? '#ffffff';
      bg.setStrokeStyle(1, getItemColorNum(bag.definitionId));

      this.panel.add(this.add.text(midX, y + 3, `🎒 ${bagDef.name}`, {
        fontSize: '10px', color: bagColor, fontFamily: FONT_BODY,
      }));
      this.panel.add(this.add.text(midX, y + 17, `+${bagDef.bonuses.capacity ?? 0} places  (${this.inv.getTransportCapacity()} total)`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));

      // Bouton déséquiper le sac → remet dans le transport
      const removeBagBtn = this.add.text(rx, y + 8, '[X]', {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 4, y: 2 },
      }).setOrigin(1, 0);
      removeBagBtn.setInteractive({ useHandCursor: true });
      removeBagBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        const removed = this.inv.unequipBackpack();
        if (removed) this.inv.addToTransport(removed);
        this.redrawSoon();
      });
      this.panel.add(removeBagBtn);
    } else {
      this.panel.add(this.add.text(midX, y + 8, '(vide — cliquer pour équiper un sac)', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    }

    return y + 36;
  }

  /** Popup : liste les sacs du transport pour équiper ou swap le sac partagé */
  private showBackpackPopup(): void {
    const matching = this.inv.getTransport().filter(i => {
      const def = ITEM_DEFINITIONS[i.definitionId];
      return def && def.slot === 'backpack';
    });

    if (matching.length === 0) {
      const flash = this.add.text(GAME_CX, GAME_CY, 'Aucun sac dans le transport', {
        fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
        backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
      }).setOrigin(0.5).setDepth(200);
      this.time.delayedCall(1500, () => flash.destroy());
      return;
    }

    this.closePopup();
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.6).setDepth(200).setInteractive();
    this.popupElements.push(overlay);

    const title = this.add.text(GAME_CX, 200, 'Équiper : 🎒 Sac à dos', {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    this.popupElements.push(title);

    let popY = 235;
    for (const item of matching) {
      const def = ITEM_DEFINITIONS[item.definitionId];
      const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
      const bonuses = formatBonuses(def.bonuses);
      const colorNum = getItemColorNum(item.definitionId);

      const itemBg = this.add.rectangle(GAME_CX, popY + 16, Math.min(400, GAME_W - 20), 34, 0x1e2d4a)
        .setStrokeStyle(1, colorNum).setDepth(201);
      const txt = this.add.text(GAME_CX - Math.min(190, GAME_W / 2 - 15), popY + 4, `🎒 ${def.name}`, {
        fontSize: '11px', color, fontFamily: FONT_BODY,
      }).setDepth(202);
      const stat = this.add.text(GAME_CX - Math.min(190, GAME_W / 2 - 15), popY + 18, bonuses || '(aucun bonus)', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setDepth(202);

      itemBg.setInteractive({ useHandCursor: true });
      itemBg.on('pointerdown', () => {
        const removed = this.inv.removeFromTransport(item.uid);
        if (!removed) return;
        const oldBag = this.inv.equipBackpack(removed);
        if (oldBag) this.inv.addToTransport(oldBag);
        this.redrawSoon();
      });
      itemBg.on('pointerover', () => itemBg.setStrokeStyle(2, 0xf1c40f));
      itemBg.on('pointerout', () => itemBg.setStrokeStyle(1, colorNum));

      this.popupElements.push(itemBg, txt, stat);
      popY += 38;
    }

    const cancelBtn = this.add.text(GAME_CX, popY + 15, '[ Annuler ]', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    ensureTouchTarget(cancelBtn);
    cancelBtn.on('pointerdown', () => this.closePopup());
    this.popupElements.push(cancelBtn);
  }

  private drawTransportItem(memberId: string, item: ItemInstance, y: number): number {
    const def = ITEM_DEFINITIONS[item.definitionId];
    if (!def) return y;
    const rowW = Math.min(540, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const color = RARITY_COLORS[def.rarity];

    const bonuses = formatBonuses(def.bonuses);
    const rowH = bonuses ? 36 : 26;
    const bg = this.add.rectangle(GAME_CX, y + rowH / 2, rowW, rowH, 0x162038);
    bg.setStrokeStyle(1, getItemColorNum(item.definitionId));
    this.panel.add(bg);

    const slotTag = SLOT_LABELS[def.slot] ?? def.slot;
    this.panel.add(this.add.text(lx, y + 3, `${slotTag}  ${def.name}`, {
      fontSize: '10px', color, fontFamily: FONT_BODY,
    }));
    if (bonuses) {
      this.panel.add(this.add.text(lx, y + 17, bonuses, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    }

    if (def.slot === 'backpack') {
      // Équiper comme sac à dos partagé
      const bagBtn = this.add.text(rx, y + 2, '[🎒 Équiper]', {
        fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 4, y: 2 },
      }).setOrigin(1, 0);
      bagBtn.setInteractive({ useHandCursor: true });
      bagBtn.on('pointerdown', () => {
        const removed = this.inv.removeFromTransport(item.uid);
        if (!removed) return;
        // L'ancien sac retourne dans le transport
        const oldBag = this.inv.equipBackpack(removed);
        if (oldBag) this.inv.addToTransport(oldBag);
        this.redrawSoon();
      });
      this.panel.add(bagBtn);
    } else {
      const btn = this.add.text(rx, y + 2, '[+]', {
        fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a2a1a', padding: { x: 4, y: 2 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        const slot = def.slot as PersonalSlot;
        // Retirer du transport
        const removed = this.inv.removeFromTransport(item.uid);
        if (!removed) return;
        // Équiper (l'ancien item revient dans le transport)
        const old = this.inv.equipItemOnCharacter(removed, memberId, slot);
        if (old) this.inv.addToTransport(old);
        this.redrawSoon();
      });
      this.panel.add(btn);
    }

    return y + rowH + 4;
  }

  shutdown(): void {
    this.closePopup();
    this.panel?.destroy();
  }

  /** Popup : affiche les items du transport équipables dans ce slot */
  private showEquipPopup(memberId: string, slot: PersonalSlot): void {
    const matching = this.inv.getTransport().filter(i => {
      const def = ITEM_DEFINITIONS[i.definitionId];
      return def && def.slot === slot;
    });

    if (matching.length === 0) {
      const flash = this.add.text(GAME_CX, GAME_CY, 'Aucun item pour ce slot dans le sac', {
        fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
        backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
      }).setOrigin(0.5).setDepth(200);
      this.time.delayedCall(1500, () => flash.destroy());
      return;
    }

    // Overlay
    this.closePopup();
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.6).setDepth(200).setInteractive();
    this.popupElements.push(overlay);

    const title = this.add.text(GAME_CX, 200, `Équiper : ${SLOT_LABELS[slot] ?? slot}`, {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    this.popupElements.push(title);

    let popY = 235;
    for (const item of matching) {
      const def = ITEM_DEFINITIONS[item.definitionId];
      const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
      const bonuses = formatBonuses(def.bonuses);
      const colorNum = getItemColorNum(item.definitionId);

      const popupW = Math.min(400, GAME_W - 20);
      const popupLx = GAME_CX - popupW / 2 + 20;
      const bg = this.add.rectangle(GAME_CX, popY + 16, popupW, 34, 0x1e2d4a).setStrokeStyle(1, colorNum).setDepth(201);
      const txt = this.add.text(popupLx, popY + 4, `${def.emoji} ${def.name}`, {
        fontSize: '11px', color, fontFamily: FONT_BODY,
      }).setDepth(202);
      const stat = this.add.text(popupLx, popY + 18, bonuses || '(aucun bonus)', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setDepth(202);

      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerdown', () => {
        // Retirer du transport et équiper
        const removed = this.inv.removeFromTransport(item.uid);
        if (!removed) return;
        const old = this.inv.equipItemOnCharacter(removed, memberId, slot);
        if (old) this.inv.addToTransport(old);
        this.redrawSoon();
      });
      bg.on('pointerover', () => bg.setStrokeStyle(2, 0xf1c40f));
      bg.on('pointerout', () => bg.setStrokeStyle(1, colorNum));

      this.popupElements.push(bg, txt, stat);
      popY += 38;
    }

    // Bouton annuler
    const cancelBtn = this.add.text(GAME_CX, popY + 15, '[ Annuler ]', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    ensureTouchTarget(cancelBtn);
    cancelBtn.on('pointerdown', () => this.closePopup());
    this.popupElements.push(cancelBtn);
  }
}
